import React, { useMemo } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
} from "react-icons/fi";
import { getVisiblePaginationPages } from "./getVisiblePaginationPages";

function cn(...parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(" ");
}

const ICON_CLASS = "h-6 w-6 shrink-0";
const ICON_DISABLED = "text-slate-300";

export type PaginationBarProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
};

function IconShell(props: {
  children: React.ReactNode;
  label: string;
  disabled: boolean;
  onPress: () => void;
}) {
  const { children, label, disabled, onPress } = props;

  if (disabled) {
    return (
      <span
        className="flex h-9 w-9 items-center justify-center"
        aria-hidden
      >
        <span className={cn(ICON_CLASS, ICON_DISABLED)}>{children}</span>
      </span>
    );
  }

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onPress}
      className="flex h-9 w-9 items-center justify-center rounded-md text-[#666666] outline-none transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-[#6EA5FF]/40"
    >
      <span className={ICON_CLASS}>{children}</span>
    </button>
  );
}

export function PaginationBar(props: PaginationBarProps) {
  const { currentPage, totalPages, onPageChange, className } = props;

  const safeTotal =
    Number.isFinite(totalPages) && totalPages >= 1 ? Math.floor(totalPages) : 0;

  const current = useMemo(() => {
    if (safeTotal < 1) return 1;
    return Math.min(
      Math.max(1, Math.floor(Number.isFinite(currentPage) ? currentPage : 1)),
      safeTotal
    );
  }, [currentPage, safeTotal]);

  const pages = useMemo(
    () => getVisiblePaginationPages(current, safeTotal),
    [current, safeTotal]
  );

  if (safeTotal < 1) {
    return null;
  }

  return (
    <nav
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-0 gap-y-2",
        className
      )}
      aria-label="Pagination"
    >
      <IconShell
        label="First page"
        disabled={current === 1}
        onPress={() => onPageChange(1)}
      >
        <FiChevronsLeft aria-hidden />
      </IconShell>
      <IconShell
        label="Previous page"
        disabled={current === 1}
        onPress={() => onPageChange(current - 1)}
      >
        <FiChevronLeft aria-hidden />
      </IconShell>

      {pages.map((page) => {
        const selected = page === current;
        return (
          <button
            key={page}
            type="button"
            aria-label={`Page ${page}`}
            aria-current={selected ? "page" : undefined}
            onClick={() => onPageChange(page)}
            className={cn(
              "mx-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xl font-bold leading-none transition-colors",
              selected
                ? "bg-[#F9F9F9] text-[#6EA5FF]"
                : "bg-transparent text-[#B6B6B6] hover:text-slate-500"
            )}
          >
            {page}
          </button>
        );
      })}

      <IconShell
        label="Next page"
        disabled={current === safeTotal}
        onPress={() => onPageChange(Math.min(safeTotal, current + 1))}
      >
        <FiChevronRight aria-hidden />
      </IconShell>
      <IconShell
        label="Last page"
        disabled={current === safeTotal}
        onPress={() => onPageChange(safeTotal)}
      >
        <FiChevronsRight aria-hidden />
      </IconShell>
    </nav>
  );
}
